import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { OnAppInit } from '@frontend/vanilla/core';
import { Action, Store } from '@ngrx/store';
import { first, merge, switchMap } from 'rxjs';

import { IConfigSettings, IUserBettingSettings, UserEvents } from '../model/user.models';
import { UserActions } from '../store/actions';
import { BettingSettingsService } from './betting-settings.service';
import { UserOddsFormatSettingsService } from './user-odds-format-settings.service';
import { UserService } from './user.service';

@Injectable({ providedIn: 'root' })
export class UserServiceWatcher implements OnAppInit {
    private userService = inject(UserService);
    private dispatcher = inject(DispatcherService);
    private store$ = inject(Store);
    private bettingSettingsService = inject(BettingSettingsService);
    private userOddsFormatSettingsService = inject(UserOddsFormatSettingsService);
    private productUtils = inject(SportsProductUtils);

    private destroyRef = inject(DestroyRef);

    onAppInit(): void {
        this.bettingSettingsService.allSettings$.pipe(first()).subscribe(({ bettingSettings, configSettings }) => {
            this.dispatch(
                UserActions.initState({
                    state: {
                        isAuthenticated: this.userService.isAuthenticated,
                        bettingSettings,
                        configSettings,
                        walletLoaded: false,
                        oddsFormat: this.userOddsFormatSettingsService.oddsFormat,
                    },
                }),
            );
        });

        this.userService.onUserLogin$.pipe(this.productUtils.filterSportsEmitLast(), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.dispatch(UserActions.userLoggedIn());
        });

        this.userService.onUserLogout$.pipe(takeUntilDestroyed(this.destroyRef), this.productUtils.filterSportsEmitLast()).subscribe(() => {
            this.dispatch(UserActions.userLoggedOut());
        });

        merge(
            this.userService.onUserLogInWithSettings$,
            this.dispatcher.on<UserService>(UserEvents.ClaimsChanged),
            this.dispatcher.on<UserService>(UserEvents.M2UserClaimsChanged),
            this.dispatcher.on(UserEvents.UserSettingsChanged),
        )
            .pipe(
                this.productUtils.filterSportsEmitLast(),
                switchMap(() => this.bettingSettingsService.allSettings$),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(({ bettingSettings, configSettings }) => {
                this.dispatchUpdateState(bettingSettings, configSettings);
            });
    }

    private dispatch(action: Action): void {
        this.store$.dispatch(action);
    }

    private dispatchUpdateState(bettingSettings: Readonly<IUserBettingSettings>, configSettings: Readonly<IConfigSettings>) {
        this.dispatch(
            UserActions.updateState({
                settings: bettingSettings,
                config: configSettings,
                oddsFormat: this.userOddsFormatSettingsService.oddsFormat,
            }),
        );
    }
}
